import { useState } from "react";

import { useTranslation } from "react-i18next";

import { toast } from "sonner";

import { useUpdatePlanningConfig } from "@/api/planningConfig";
import type { PlanningConfig } from "@/api/planningConfig";
import { usePlanningPolicies } from "@/api/planningPolicy";
import { CommittedInput } from "@/components/committed-input";
import { PlanningPolicyCombobox } from "@/components/entity-select/PlanningPolicyCombobox";
import { Label } from "@/components/ui/label";

interface PlanningPolicySectionProps {
  itemId: number;
  locationId: number;
  config: PlanningConfig | null;
}

interface PendingPick {
  policy: number;
  minStock: number | null;
  maxStock: number | null;
}

export function PlanningPolicySection({
  itemId,
  locationId,
  config,
}: PlanningPolicySectionProps) {
  const { t } = useTranslation();
  const policies = usePlanningPolicies();
  const updateConfig = useUpdatePlanningConfig();
  const node = { item: itemId, location: locationId };
  // A min_max policy can't be saved without numbers. Picking one on a row that
  // has none parks the pick here; it commits itself once min < max are typed.
  const [pending, setPending] = useState<PendingPick | null>(null);

  const regimeOf = (id: number | null | undefined) =>
    policies.data?.find((p) => p.id === id)?.regime;
  const current = config?.planningPolicy ?? null;
  const hasNumbers = config?.minStock != null && config?.maxStock != null;

  const callbacks = {
    onSuccess: () => setPending(null),
    onError: () => toast.error(t("items.detail.planningPolicy.saveFailed")),
  };
  const patch = (
    body: Omit<Parameters<typeof updateConfig.mutate>[0], "item" | "location">,
  ) => updateConfig.mutate({ ...node, ...body }, callbacks);
  const assign = (
    planningPolicy: number | null,
    numbers?: { minStock: number; maxStock: number },
  ) => patch({ planningPolicy, ...numbers });

  function pick(policyId: number | null) {
    if (policyId === current) {
      setPending(null);
      return;
    }
    if (policyId !== null && regimeOf(policyId) === "min_max" && !hasNumbers) {
      setPending({ policy: policyId, minStock: null, maxStock: null });
      return;
    }
    assign(policyId);
  }

  function commitPending(next: Partial<PendingPick>) {
    if (!pending) return;
    const merged = { ...pending, ...next };
    setPending(merged);
    if (
      merged.minStock !== null &&
      merged.maxStock !== null &&
      merged.minStock < merged.maxStock
    )
      assign(merged.policy, {
        minStock: merged.minStock,
        maxStock: merged.maxStock,
      });
  }

  const parse = (raw: string) => (raw === "" ? null : Number(raw));
  const showNumbers =
    pending !== null || (config !== null && regimeOf(current) === "min_max");
  const fields = [
    {
      id: "min-stock",
      label: t("items.detail.planningPolicy.min"),
      value: pending ? pending.minStock : (config?.minStock ?? null),
      commit: (raw: string) =>
        pending
          ? commitPending({ minStock: parse(raw) })
          : patch({ minStock: parse(raw) }),
    },
    {
      id: "max-stock",
      label: t("items.detail.planningPolicy.max"),
      value: pending ? pending.maxStock : (config?.maxStock ?? null),
      commit: (raw: string) =>
        pending
          ? commitPending({ maxStock: parse(raw) })
          : patch({ maxStock: parse(raw) }),
    },
  ];

  return (
    <div>
      <div className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">
        {t("items.detail.planningPolicy.sectionTitle")}
      </div>
      <PlanningPolicyCombobox
        value={pending?.policy ?? current}
        onChange={pick}
        variant="ghost"
        triggerClassName="-ml-2"
        emptyLabel={t("items.detail.planningPolicy.notPlanned")}
      />

      {showNumbers && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {fields.map((f) => (
            <div key={f.id} className="flex flex-col gap-1">
              <Label htmlFor={f.id} className="text-xs">
                {f.label}
              </Label>
              <CommittedInput
                id={f.id}
                type="number"
                min={0}
                className="h-7 text-xs"
                value={f.value === null ? "" : String(f.value)}
                onCommit={f.commit}
              />
            </div>
          ))}
        </div>
      )}
      {pending && (
        <p className="text-muted-foreground mt-2 text-xs">
          {t("items.detail.planningPolicy.pendingHint")}
        </p>
      )}
    </div>
  );
}
